import React from 'react'
import { Box, Typography } from '@mui/material'
import { styled } from '@mui/system'
import { motion } from 'framer-motion'
import { getSeatConfig, calculateSeatLayout } from './seatConfigs'

// Styled Components
const SeatLayoutContainer = styled(Box)(({ config }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  padding: '24px',
  maxWidth: config.visual.containerWidth,
  margin: '0 auto',
  position: 'relative'
}))

const DriverSection = styled(Box)(({ config }) => ({
  display: 'flex',
  justifyContent: config.layout.driverPosition === 'front-left' ? 'flex-start' : 'flex-end',
  width: '100%',
  marginBottom: '24px',
  paddingLeft: config.layout.driverPosition === 'front-left' ? '20px' : '0',
  paddingRight: config.layout.driverPosition === 'front-right' ? '20px' : '0'
}))

const MainSeatingArea = styled(Box)(({ config }) => ({
  display: 'flex',
  gap: config.visual.gapBetweenColumns,
  width: '100%',
  justifyContent: 'center',
  alignItems: 'flex-start'
}))

const SeatColumn = styled(Box)(({ config }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: config.visual.gapBetweenRows,
  alignItems: 'center'
}))

const SeatRow = styled(Box)(({ seatsPerRow, config }) => ({
  display: 'grid',
  gridTemplateColumns: `repeat(${seatsPerRow}, ${config.visual.seatSize})`,
  gap: '8px',
  justifyContent: 'center'
}))

const BackRowContainer = styled(Box)(({ config }) => ({
  display: 'flex',
  justifyContent: 'center',
  marginTop: '20px',
  width: '100%'
}))

const SeatButton = styled(motion.button)(({ seatType, isSelected, isDisabled, config }) => ({
  width: config.visual.seatSize,
  height: config.visual.seatSize,
  padding: 0,
  border: 'none',
  background: 'none',
  cursor: isDisabled ? 'not-allowed' : 'pointer',
  position: 'relative',
  outline: isSelected ? '2px solid #ef404f' : 'none',
  borderRadius: '6px',
  opacity: isDisabled ? 0.5 : 1,
  '&:disabled': {
    cursor: 'not-allowed',
    opacity: 0.5,
  },
}))

const SeatNumberLabel = styled(Typography)(({ config }) => ({
  position: 'absolute',
  bottom: '-18px',
  left: '50%',
  transform: 'translateX(-50%)',
  fontSize: '10px',
  fontWeight: '600',
  color: '#666',
  userSelect: 'none'
}))

// Seat icon mappings
const seatIcons = {
  'first-class': '/images/tealseatbig.svg',
  premium: '/images/orangeseatb.svg',
  economy: '/images/blueseat.svg',
  reserved: '/images/reservedseatbig.svg',
  driver: '/images/steeringwheel.svg',
}

const SeatIcon = ({ type, isReserved, config }) => {
  const iconPath = isReserved ? seatIcons.reserved : seatIcons[type]
  
  return (
    <img
      src={iconPath}
      alt={`${type} seat`}
      style={{ 
        width: config.visual.seatSize, 
        height: config.visual.seatSize,
        display: 'block'
      }}
    />
  )
}

const DriverIcon = ({ config }) => (
  <img
    src={seatIcons.driver}
    alt="Driver seat"
    style={{ 
      width: config.visual.seatSize, 
      height: config.visual.seatSize 
    }}
  />
)

/**
 * Optimized Seat Layout Component
 * Supports all vehicle configurations: 11, 14, 16, 25, 33, 65 seaters
 */
const OptimizedSeatLayout = ({ 
  seatData = [], 
  selectedSeats = [], 
  onSeatClick,
  className = ''
}) => {
  // Determine seat count and get configuration
  const seatCount = seatData.length
  const config = getSeatConfig(seatCount)
  const layout = calculateSeatLayout(seatCount, seatData)

  // Animation variants
  const seatVariants = {
    hover: { scale: 1.05 },
    tap: { scale: 0.95 }
  }

  // Check if seat is selected
  const isSeatSelected = (seat) => {
    return selectedSeats.some(s => s.id === seat.id)
  }

  // Check if seat is disabled
  const isSeatDisabled = (seat) => {
    return seat.isReserved || seat.booked || seat.payedFor || seat.selected
  }

  // Render individual seat
  const renderSeat = (seat) => (
    <SeatButton
      key={`seat-${seat.id}`}
      seatType={seat.category}
      isSelected={isSeatSelected(seat)}
      isDisabled={isSeatDisabled(seat)}
      config={config}
      disabled={isSeatDisabled(seat)}
      onClick={() => onSeatClick && onSeatClick(seat)}
      variants={seatVariants}
      whileHover={!isSeatDisabled(seat) ? "hover" : {}}
      whileTap={!isSeatDisabled(seat) ? "tap" : {}}
    >
      <SeatIcon
        type={seat.category}
        isReserved={isSeatDisabled(seat)}
        config={config}
      />
      <SeatNumberLabel config={config}>
        {seat.id}
      </SeatNumberLabel>
    </SeatButton>
  )

  // Render seat row
  const renderSeatRow = (seats, rowIndex, columnType) => (
    <SeatRow 
      key={`${columnType}-row-${rowIndex}`}
      seatsPerRow={seats.length}
      config={config}
    >
      {seats.map(seat => renderSeat(seat))}
    </SeatRow>
  )

  return (
    <SeatLayoutContainer config={config} className={className}>
      {/* Driver Section */}
      <DriverSection config={config}>
        <DriverIcon config={config} />
      </DriverSection>

      {/* Main Seating Area */}
      <MainSeatingArea config={config}>
        {/* Left Column */}
        {layout.leftColumn.length > 0 && (
          <SeatColumn config={config}>
            {layout.leftColumn.map((rowSeats, rowIndex) => 
              renderSeatRow(rowSeats, rowIndex, 'left')
            )}
          </SeatColumn>
        )}

        {/* Right Column */}
        {layout.rightColumn.length > 0 && (
          <SeatColumn config={config}>
            {layout.rightColumn.map((rowSeats, rowIndex) => 
              renderSeatRow(rowSeats, rowIndex, 'right')
            )}
          </SeatColumn>
        )}
      </MainSeatingArea>

      {/* Back Row (for configurations that have it) */}
      {layout.backRow.length > 0 && (
        <BackRowContainer config={config}>
          <SeatRow 
            seatsPerRow={layout.backRow.length}
            config={config}
          >
            {layout.backRow.map(seat => renderSeat(seat))}
          </SeatRow>
        </BackRowContainer>
      )}

      {/* Configuration Info for Development */}
      {process.env.NODE_ENV === 'development' && (
        <Box sx={{ mt: 2, p: 1, bgcolor: '#f5f5f5', borderRadius: 1 }}>
          <Typography variant="caption" color="textSecondary">
            {config.name} ({seatCount} seats)
          </Typography>
        </Box>
      )}
    </SeatLayoutContainer>
  )
}

export default OptimizedSeatLayout